// As MESMAS contas do EmailBuilder::LayoutContext (servidor), para o canvas
// mostrar o e-mail como ele vai sair: cores do tema, contraste automático,
// fontes com reserva e larguras reais em px.
const HEX = /^#[0-9a-f]{6}$/i;
export const MUTED = '#6b7a90';
const SERIF = [
  'Georgia',
  'Times New Roman',
  'Merriweather',
  'Playfair Display',
];

const luminance = hex => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const themeColor = (value, theme = {}) => {
  if (HEX.test(value || '')) return value;
  const map = {
    primary: theme.primary,
    accent: theme.accent,
    text: theme.text,
    muted: MUTED,
    surface: theme.surface,
    white: '#ffffff',
    dark: '#0d1a2e',
  };
  return map[value] || null;
};

export const onDark = bgValue => {
  if (['primary', 'accent', 'dark'].includes(bgValue)) return true;
  return HEX.test(bgValue || '') ? luminance(bgValue) < 0.45 : false;
};

export const textColor = (value, bgValue, theme = {}) => {
  if (value === 'muted') {
    return onDark(bgValue) ? 'rgba(255,255,255,0.8)' : MUTED;
  }
  return (
    themeColor(value, theme) ||
    (onDark(bgValue) ? '#ffffff' : theme.text || '#0a2540')
  );
};

export const contrast = hex => (luminance(hex) < 0.6 ? '#ffffff' : '#111827');

export const fontStack = (name, doc = {}, theme = {}, heading = false) => {
  const fam =
    name ||
    doc?.settings?.font_family ||
    doc?.font_family ||
    theme[heading ? 'font_heading' : 'font_body'] ||
    'Arial';
  const fallback = SERIF.includes(fam)
    ? "Georgia, 'Times New Roman', serif"
    : 'Arial, Helvetica, sans-serif';
  return `'${fam}', ${fallback}`;
};

export const paddingCss = (
  box = [0, 0, 0, 0],
  { mobile = false, row = false } = {}
) => {
  const b = [...box];
  if (mobile && row) {
    b[1] = 20;
    b[3] = 20;
  }
  return b.map(v => `${v}px`).join(' ');
};

export const docWidth = (doc, device) =>
  device === 'mobile'
    ? 375
    : Math.min(800, Math.max(480, doc?.settings?.width || 600));

const side = (box, i) => (box && box[i]) || 0;

export const columnPx = (width, row, col) =>
  Math.floor(
    ((width - side(row.style?.padding, 1) - side(row.style?.padding, 3)) *
      col.width) /
      100
  );

export const columnInnerPx = (width, row, col) =>
  columnPx(width, row, col) -
  side(col.style?.padding, 1) -
  side(col.style?.padding, 3);

// Largura útil da coluna como o servidor calcula. No celular a linha sempre
// tem 20px de cada lado; empilhada, a coluna ocupa a largura toda.
export const columnInnerFor = (doc, row, col, device, stacked) => {
  const colSides = side(col.style?.padding, 1) + side(col.style?.padding, 3);
  if (device !== 'mobile') {
    return columnInnerPx(docWidth(doc, 'desktop'), row, col);
  }
  const width = docWidth(doc, 'mobile');
  if (stacked) return width - 40 - colSides;
  const mobileRow = {
    ...row,
    style: { ...row.style, padding: [0, 20, 0, 20] },
  };
  return columnInnerPx(width, mobileRow, col);
};

export const imagePx = (element, inner) => {
  const avail =
    inner - side(element.style?.padding, 1) - side(element.style?.padding, 3);
  const w = element.width || {};
  const px =
    w.unit === 'px'
      ? Number(w.value)
      : Math.round((avail * Number(w.value)) / 100);
  return Math.max(1, Math.min(px, Math.max(avail, 1)));
};

export const buttonColors = (style = {}, theme = {}) => {
  const bg = themeColor(style.bg, theme) || theme.primary || '#1f93ff';
  return {
    bg,
    fg: style.color ? themeColor(style.color, theme) : contrast(bg),
  };
};

export const borderStyle = (border, theme = {}) => {
  if (!border || !border.width) return {};
  const value = `${border.width}px solid ${themeColor(border.color, theme) || '#e6e9ee'}`;
  return border.sides === 'left' ? { borderLeft: value } : { border: value };
};

export const isHidden = (style, device) =>
  (style?.hide_on === 'mobile' && device === 'mobile') ||
  (style?.hide_on === 'desktop' && device === 'desktop');
